import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { invokeEdgeFunction } from '../../lib/supabaseEdge'
import { useAuth } from '../../lib/AuthContext'
import {
  pageWrap,
  pageTitle,
  pageSubtitle,
  pageHint,
  glassToolbarCard,
  glassDividerLine,
  glassReportPanel,
  glassPageBackground,
  glassInputStyle,
  infoBanner,
  emptyState,
  btnPrimary,
  btnSecondary,
  actionsRow,
} from './knowledgePageLayout'
import { KbSearchResultCard, hybridMetaBannerText } from './kbSearchResultUi'
import { glassPanelStyle } from './agent/agentChatLayout'
import AgentHeroCard from './agent/AgentHeroCard'

const dividerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  margin: '8px 0 24px',
  color: '#78716c',
  fontSize: 13,
  fontWeight: 500,
}

const warmTitle = {
  ...pageTitle,
  color: '#1c1917',
}

const warmSubtitle = {
  ...pageSubtitle,
  color: '#78716c',
}

const warmHint = {
  ...pageHint,
  color: '#a8a29e',
}

const WIKI_STATUS_STYLE = {
  published: { bg: '#dcfce7', color: '#15803d' },
  needs_review: { bg: '#fef3c7', color: '#b45309' },
}

const reportHtmlStyle = {
  fontSize: 14,
  color: '#44403c',
  lineHeight: 1.6,
}

/**
 * User-facing hybrid KB search with citations + optional client filter + feedback.
 */
export default function KnowledgeSearch() {
  const { user, profile, canAccessPage } = useAuth()
  const isSuperAdmin = profile?.role === 'super_admin'
  const canOpenPlayground = canAccessPage('knowledge-base:access')
  const canOpenWiki = canAccessPage('knowledge-base:wiki')
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(() => searchParams.get('q') || '')
  const [clientId, setClientId] = useState('')
  const [clients, setClients] = useState([])
  const [includePersonal, setIncludePersonal] = useState(
    () => searchParams.get('personal') === '1'
  )
  const [personalFlagOn, setPersonalFlagOn] = useState(false)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState([])
  const [meta, setMeta] = useState(null)
  const [lastQuery, setLastQuery] = useState('')
  const [feedback, setFeedback] = useState({})
  const [wikiContext, setWikiContext] = useState(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [analyzeError, setAnalyzeError] = useState('')
  const [reportHtml, setReportHtml] = useState('')
  const [reportSections, setReportSections] = useState(null)
  const [searchLatencyId, setSearchLatencyId] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const cfgPromise = supabase
        .from('kb_pipeline_configs')
        .select('enable_personal_knowledge')
        .is('source_id', null)
        .maybeSingle()

      let clientList = []
      if (isSuperAdmin) {
        const { data } = await supabase
          .from('clients')
          .select('id, client_name')
          .order('client_name')
          .limit(500)
        clientList = data ?? []
      } else if (user?.id) {
        const { data } = await supabase
          .from('kb_user_client_grants')
          .select('client_id, clients(id, client_name)')
          .eq('user_id', user.id)
        clientList = (data ?? [])
          .map((row) => {
            const c = row.clients
            if (!c) return null
            return { id: c.id, client_name: c.client_name }
          })
          .filter(Boolean)
          .sort((a, b) =>
            String(a.client_name || '').localeCompare(String(b.client_name || ''), undefined, {
              sensitivity: 'base',
            })
          )
      }

      const cfgRes = await cfgPromise
      if (cancelled) return
      setClients(clientList)
      setClientId((prev) => (prev && !clientList.some((c) => c.id === prev) ? '' : prev))
      const flag = cfgRes.data?.enable_personal_knowledge === true
      setPersonalFlagOn(flag)
      if (!flag) setIncludePersonal(false)
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id, isSuperAdmin])

  const runSearch = async () => {
    const q = query.trim()
    if (!q) return
    setSearching(true)
    setError('')
    setResults([])
    setMeta(null)
    setFeedback({})
    setLastQuery(q)
    setWikiContext(null)
    setAnalyzeError('')
    setReportHtml('')
    setReportSections(null)
    setSearchLatencyId('')
    try {
      const bodyPayload = { query: q, channel: 'search' }
      if (clientId) bodyPayload.client_id = clientId
      if (personalFlagOn && includePersonal) bodyPayload.include_personal = true
      const res = await invokeEdgeFunction('kb-search', {
        method: 'POST',
        body: JSON.stringify(bodyPayload),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.message ?? `Search failed (${res.status})`)
      setResults(body.results ?? [])
      setMeta({
        hybrid: body.hybrid,
        arms: body.arms,
        vector_error: body.vector_error,
        match_threshold: body.match_threshold,
      })
      setWikiContext(body.wiki_context ?? null)
      setSearchLatencyId(body.latency_id ? String(body.latency_id) : '')
    } catch (e) {
      setError(e?.message ?? 'Search failed')
    } finally {
      setSearching(false)
    }
  }

  const runAnalysis = async () => {
    if (analyzing || results.length === 0 || !lastQuery.trim()) return
    setAnalyzing(true)
    setAnalyzeError('')
    try {
      const res = await invokeEdgeFunction('kb-search-analyze', {
        method: 'POST',
        body: JSON.stringify({
          query: lastQuery,
          latency_id: searchLatencyId || undefined,
          results: results.map((r) => ({
            chunk_id: r.chunk_id,
            document_id: r.document_id,
            title: r.title,
            chunk_text: r.chunk_text,
            section_heading: r.section_heading,
            library: r.library,
          })),
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        const hint =
          body.hint_code === 'MISSING_LLM_KEY' || body.hint_code === 'MISSING_OPENAI_KEY'
            ? ' Configure LLM routing and API keys under Settings → Integrations.'
            : ''
        throw new Error(`${body.message ?? `Analysis failed (${res.status})`}${hint}`)
      }
      setReportHtml(String(body.html_report || ''))
      setReportSections(body.sections ?? null)
    } catch (e) {
      setAnalyzeError(e?.message ?? 'Analysis failed')
      setReportHtml('')
      setReportSections(null)
    } finally {
      setAnalyzing(false)
    }
  }

  const submitFeedback = async (result, helpful) => {
    if (!user?.id || feedback[result.chunk_id] != null) return
    setFeedback((prev) => ({ ...prev, [result.chunk_id]: helpful }))
    const { error: fbErr } = await supabase.from('kb_search_feedback').insert({
      user_id: user.id,
      query: lastQuery,
      chunk_id: result.chunk_id,
      document_id: result.document_id,
      helpful,
    })
    if (fbErr) {
      setFeedback((prev) => {
        const next = { ...prev }
        delete next[result.chunk_id]
        return next
      })
      setError(`Feedback failed: ${fbErr.message}`)
    }
  }

  return (
    <div style={{ ...pageWrap, ...glassPageBackground }}>
      <h1 style={warmTitle}>Knowledge</h1>
      <p style={warmSubtitle}>Smart agent + hybrid search across wiki, uploads, and federated sources</p>
      <p style={warmHint}>
        The assistant retrieves and summarizes within your permissions. Manual search below returns
        raw hybrid results with citations.
        {canOpenPlayground ? (
          <>
            {' '}
            Admins can also use the{' '}
            <Link to="/admin/knowledge/access?tab=playground" style={{ color: '#c17f59' }}>
              Playground
            </Link>
            .
          </>
        ) : null}
        {personalFlagOn ? (
          <>
            {' '}
            Manage private files in{' '}
            <Link to="/personal-knowledge" style={{ color: '#c17f59' }}>
              Personal Library
            </Link>
            .
          </>
        ) : null}
      </p>

      <AgentHeroCard
        clientId={clientId}
        includePersonal={includePersonal}
        canOpenWiki={canOpenWiki}
      />

      <div style={dividerStyle}>
        <span style={glassDividerLine} />
        Manual search
        <span style={glassDividerLine} />
      </div>

      <h2 style={{ ...warmTitle, fontSize: 20, marginBottom: 8 }}>Manual search</h2>
      <p style={{ ...warmHint, marginTop: 0 }}>
        Hybrid keyword + vector search with citations. Optional client filter scopes tagged docs you
        are granted.
      </p>

      <div style={glassToolbarCard}>
        <div style={{ ...actionsRow, flexWrap: 'wrap', borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch()
            }}
            placeholder="Search SOPs, contracts, Basecamp, digests…"
            style={{
              flex: 1,
              minWidth: 220,
              padding: '12px 14px',
              borderRadius: 10,
              fontSize: 15,
              ...glassInputStyle,
            }}
          />
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            style={{
              padding: '12px 10px',
              borderRadius: 10,
              fontSize: 14,
              minWidth: 180,
              maxWidth: 260,
              ...glassInputStyle,
            }}
            title={
              isSuperAdmin
                ? 'Filter to a client (org-wide docs still included)'
                : clients.length === 0
                  ? 'No client grants — searches org-wide docs only'
                  : 'Filter to a granted client (org-wide docs still included)'
            }
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.client_name || c.id}
              </option>
            ))}
          </select>
          <button
            type="button"
            style={{ ...btnPrimary, background: '#c17f59', border: 'none', borderRadius: 10 }}
            disabled={searching || !query.trim()}
            onClick={runSearch}
          >
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>

        {personalFlagOn ? (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 12,
              fontSize: 13,
              color: '#334155',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={includePersonal}
              onChange={(e) => setIncludePersonal(e.target.checked)}
            />
            Personal — also search my private library (does not unlock org modules)
          </label>
        ) : null}

        {meta ? (
          <div style={{ ...infoBanner, marginTop: 12, borderColor: '#bfdbfe', background: '#eff6ff', color: '#1e40af' }}>
            {hybridMetaBannerText(meta)}
          </div>
        ) : null}

        {error ? (
          <div style={{ ...infoBanner, marginTop: 12, borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c' }}>
            {error}
          </div>
        ) : null}
      </div>

      {wikiContext ? (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            ...glassPanelStyle,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#c17f59', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Wiki context
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                padding: '2px 8px',
                borderRadius: 999,
                background: (WIKI_STATUS_STYLE[wikiContext.status] || WIKI_STATUS_STYLE.needs_review).bg,
                color: (WIKI_STATUS_STYLE[wikiContext.status] || WIKI_STATUS_STYLE.needs_review).color,
              }}
            >
              {wikiContext.status}
            </span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginTop: 6, color: '#1c1917' }}>{wikiContext.title}</div>
          {wikiContext.summary ? (
            <div style={{ fontSize: 13, color: '#78716c', marginTop: 4 }}>{wikiContext.summary}</div>
          ) : null}
          {canOpenWiki ? (
            <Link
              to={`/admin/knowledge/wiki?tab=wiki&slug=${encodeURIComponent(wikiContext.slug)}`}
              style={{ fontSize: 12, color: '#c17f59', marginTop: 6, display: 'inline-block' }}
            >
              View wiki page →
            </Link>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
        {!searching && meta && results.length === 0 ? <div style={emptyState}>No results.</div> : null}
        {results.map((r, idx) => (
          <KbSearchResultCard
            key={r.chunk_id}
            result={r}
            rank={idx + 1}
            query={lastQuery}
            snippetMax={400}
            onFeedback={(helpful) => submitFeedback(r, helpful)}
            feedbackValue={feedback[r.chunk_id] ?? null}
          />
        ))}
      </div>

      {results.length > 0 ? (
        <div style={{ marginTop: 16, ...actionsRow, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            style={{ ...btnPrimary, background: '#c17f59', border: 'none', borderRadius: 10 }}
            disabled={analyzing || searching}
            onClick={runAnalysis}
          >
            {analyzing ? 'Analyzing…' : 'AI Analysis'}
          </button>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            Analyzes the {results.length} result{results.length === 1 ? '' : 's'} above (snippets only).
          </span>
        </div>
      ) : null}

      {analyzeError ? (
        <div
          style={{
            ...infoBanner,
            marginTop: 12,
            borderColor: '#fecaca',
            background: '#fef2f2',
            color: '#b91c1c',
          }}
        >
          {analyzeError}
        </div>
      ) : null}

      {reportHtml || analyzing ? (
        <div style={glassReportPanel}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 12,
              marginBottom: 12,
              borderBottom: '1px solid #f1f5f9',
              paddingBottom: 10,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
              {reportSections?.title || 'AI Analysis Report'}
            </div>
            {analyzing ? (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Working…</span>
            ) : (
              <button
                type="button"
                style={{ ...btnSecondary, fontSize: 12, padding: '4px 10px' }}
                onClick={() => {
                  setReportHtml('')
                  setReportSections(null)
                  setAnalyzeError('')
                }}
              >
                Clear
              </button>
            )}
          </div>
          {analyzing && !reportHtml ? (
            <div style={{ ...emptyState, margin: 0, padding: '24px 0' }}>
              Generating professional report from retrieved snippets…
            </div>
          ) : (
            <div
              style={reportHtmlStyle}
              className="kb-ai-analysis-report"
              // Server-built HTML is escaped; only safe structural tags.
              dangerouslySetInnerHTML={{ __html: reportHtml }}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}
